import React, { useState } from 'react';
import type { Coexistence, SetupDecision } from '@/lib/emailAuth';

/**
 * Admin → Email delivery → GoDaddy DNS guide → two safety blocks.
 *
 * 1. LEAVE ALONE — every row in the zone that belongs to another system.
 *    giggone.com already has a GoHighLevel / LeadConnector dedicated sending
 *    subdomain (mail.giggone.com). Its DKIM private key lives in GoHighLevel's
 *    account, so reusing that subdomain would mint a second key pair for the
 *    SAME hostname and break whichever system did not publish the winning
 *    public key. Its MX also routes CRM reply capture. So: never delete, never
 *    reuse, never add it to our own provider account.
 *
 * 2. SETUP DECISIONS — the answers to the sending-domain questions, with the
 *    reasoning attached, rendered straight from the verifier so the panel and
 *    the server can never drift apart.
 */

const AdminDnsCoexistence: React.FC<{ coexistence: Coexistence; decisions: SetupDecision[]; es: boolean }> = ({
  coexistence,
  decisions,
  es,
}) => {
  const [open, setOpen] = useState<number | null>(0);

  return (
    <>
      {coexistence.detected && (
        <div className="mt-6 rounded-xl border border-amber-300 bg-amber-50 p-4">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-extrabold uppercase tracking-wide text-amber-900">
              {es ? 'No tocar' : 'Leave alone'}
            </h3>
            <span className="rounded-full bg-amber-900 px-2 py-0.5 text-[10px] font-bold uppercase text-white">
              {coexistence.subdomain}
            </span>
            <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-bold uppercase text-amber-800 ring-1 ring-amber-300">
              {coexistence.confidence === 'confirmed'
                ? es
                  ? 'CONFIRMADO EN DNS'
                  : 'CONFIRMED IN DNS'
                : es
                  ? 'PROBABLE'
                  : 'LIKELY'}
            </span>
          </div>

          <p className="mt-2 text-sm font-bold text-amber-900">{coexistence.verdict}</p>
          <p className="mt-1 text-xs leading-relaxed text-amber-800">
            <span className="font-bold">{es ? 'Propietario: ' : 'Owner: '}</span>
            {coexistence.owner}
          </p>
          <p className="mt-2 text-xs leading-relaxed text-amber-800">{coexistence.reason}</p>

          <div className="mt-3 space-y-2">
            {coexistence.items.map((it) => (
              <div key={it.fqdn + it.type} className="rounded-lg bg-white p-2.5 ring-1 ring-amber-200">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded bg-slate-900 px-1.5 py-0.5 text-[10px] font-bold text-white">{it.type}</span>
                  <code className="font-mono text-[11px] font-bold text-slate-800">{it.fqdn}</code>
                  <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-700 ring-1 ring-emerald-200">
                    {es ? 'CONSERVAR' : 'KEEP'}
                  </span>
                </div>
                {it.value && (
                  <code className="mt-1 block break-all font-mono text-[10px] text-slate-500">{it.value}</code>
                )}
                <p className="mt-1 text-[11px] leading-relaxed text-slate-600">{it.why}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {decisions.length > 0 && (
        <>
          <h3 className="mt-6 text-sm font-bold uppercase tracking-wide text-slate-500">
            {es ? 'Decisiones de configuración' : 'Setup decisions'}
          </h3>
          <div className="mt-2 divide-y divide-slate-200 overflow-hidden rounded-xl border border-slate-200">
            {decisions.map((d, i) => (
              <div key={d.q}>
                <button
                  onClick={() => setOpen(open === i ? null : i)}
                  className="flex w-full items-start gap-3 bg-white px-3 py-2.5 text-left transition hover:bg-slate-50"
                >
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-900 text-[10px] font-bold text-white">
                    {i + 1}
                  </span>
                  <span className="flex-1">
                    <span className="block text-xs font-bold text-slate-900">{d.q}</span>
                    <span className="mt-0.5 block text-xs font-semibold text-emerald-700">{d.a}</span>
                  </span>
                  <span className="mt-0.5 shrink-0 text-[10px] font-bold text-slate-400">
                    {open === i ? '−' : es ? 'POR QUÉ' : 'WHY'}
                  </span>
                </button>
                {open === i && <p className="bg-slate-50 px-3 pb-3 pl-11 text-[11px] leading-relaxed text-slate-600">{d.why}</p>}
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
};

export default AdminDnsCoexistence;
